import { useState } from 'react'
import { toast } from 'sonner'
import { Loader2, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { LANGS, DEFAULT_LANG } from '@/i18n/config'
import { CURRENCIES } from '@/lib/currency'
import { useCompleteOnboarding } from '@/hooks/useOnboarding'

// Primeiro acesso de um household novo: obriga escolher idioma + moeda
// principal antes de liberar o resto do app. Texto fixo em PT-BR de propósito
// (é exibido ANTES do idioma ser confirmado) — mesmo padrão de LoginPage.tsx.
export function OnboardingDialog() {
  const complete = useCompleteOnboarding()
  const [language, setLanguage] = useState<string>(DEFAULT_LANG)
  const [baseCurrency, setBaseCurrency] = useState<string>('')

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!baseCurrency) return
    try {
      await complete.mutateAsync({ language, baseCurrency })
    } catch (err) {
      toast.error('Não foi possível salvar', { description: (err as Error).message })
    }
  }

  return (
    <div className="flex min-h-svh items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardContent className="p-6">
          <div className="mb-6 flex items-center gap-2">
            <div className="bg-primary text-primary-foreground flex size-9 items-center justify-center rounded-lg">
              <Sparkles className="size-5" />
            </div>
            <div>
              <div className="leading-tight font-semibold">Bem-vindo(a) ao EasyMoney</div>
              <div className="text-muted-foreground text-xs">Vamos configurar o básico antes de começar</div>
            </div>
          </div>

          <form onSubmit={onSubmit} className="grid gap-4">
            <div className="grid gap-2">
              <Label>Idioma</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LANGS.map((l) => (
                    <SelectItem key={l.code} value={l.code}>
                      {l.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label>Moeda principal</Label>
              <Select value={baseCurrency} onValueChange={setBaseCurrency}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecione a moeda" />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.code} — {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-muted-foreground text-xs">
                É a moeda em que os totais consolidados vão aparecer. Dá para adicionar outras moedas e trocar isso depois.
              </p>
            </div>

            <Button type="submit" disabled={!baseCurrency || complete.isPending}>
              {complete.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
              {complete.isPending ? 'Salvando…' : 'Começar'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
